'use client';

import React, { createContext, useContext, useLayoutEffect, useState } from 'react';

import { usePathname } from 'next/navigation';

import { useUserInfo } from '@/context/UserInfoContext';

import { deriveScene } from './ontology/scene';
import type { Scene } from './ontology/types';

/** Stores the current feedback scene derived from baseline resets and tracked-route setters. */
const SceneContext = createContext<Scene | undefined>(undefined);

/** Stores the scene setter used by tracked routes to set authoritative scene context. */
const SetSceneContext = createContext<React.Dispatch<React.SetStateAction<Scene>> | undefined>(
  undefined
);

/**
 * Stores feedback scene state centrally, resetting to the baseline scene on
 * pathname or auth changes before tracked routes set richer scene context.
 */
export const SceneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname: string = usePathname();
  const { userInfo } = useUserInfo();
  const isLoggedIn = !!userInfo;
  const [scene, setScene] = useState<Scene>(() => deriveScene(pathname, isLoggedIn));

  useLayoutEffect(() => {
    setScene(deriveScene(pathname, isLoggedIn));
  }, [pathname, isLoggedIn]);

  return (
    <SetSceneContext.Provider value={setScene}>
      <SceneContext.Provider value={scene}>{children}</SceneContext.Provider>
    </SetSceneContext.Provider>
  );
};

/** Returns the current feedback scene and requires a surrounding SceneProvider. */
export const useScene = (): Scene => {
  const context = useContext(SceneContext);
  if (context === undefined) {
    throw new Error('useScene must be used within a SceneProvider');
  }
  return context;
};

/** Returns the scene setter for tracked routes that set authoritative scene context. */
export const useSetScene = (): React.Dispatch<React.SetStateAction<Scene>> => {
  const context = useContext(SetSceneContext);
  if (context === undefined) {
    throw new Error('useSetScene must be used within a SceneProvider');
  }
  return context;
};
